import { AxiosResponse } from "axios";

import { axiosInstance } from "./common";
import { ExportFormat, extractFileDownload } from "./download";

export enum FraudElementCategories {
  FALSE_IDENTITY_OR_KYC_AT_ONBOARDING = "FALSE_IDENTITY_OR_KYC_AT_ONBOARDING",
  FORGED_KYC_DOCUMENTS = "FORGED_KYC_DOCUMENTS",
  IMPERSONATION_OF_BORROWER = "IMPERSONATION_OF_BORROWER",
  FABRICATED_FINANCIAL_STATEMENTS = "FABRICATED_FINANCIAL_STATEMENTS",
  INFLATED_TURNOVER_OR_SALES = "INFLATED_TURNOVER_OR_SALES",
  INFLATION_OF_PROJECT_COST = "INFLATION_OF_PROJECT_COST",
  INFLATION_OF_ASSET_OR_MACHINERY_VALUE = "INFLATION_OF_ASSET_OR_MACHINERY_VALUE",
  OVERVALUATION_OF_COLLATERAL = "OVERVALUATION_OF_COLLATERAL",
  FAKE_OR_SPURIOUS_COLLATERAL = "FAKE_OR_SPURIOUS_COLLATERAL",
  MULTIPLE_MORTGAGE_OF_SAME_PROPERTY = "MULTIPLE_MORTGAGE_OF_SAME_PROPERTY",
  MULTIPLE_FINANCING_SAME_COLLATERAL = "MULTIPLE_FINANCING_SAME_COLLATERAL",
  SUPPRESSION_OF_EXISTING_LIABILITIES = "SUPPRESSION_OF_EXISTING_LIABILITIES",
  CONCEALMENT_OF_MATERIAL_FACTS = "CONCEALMENT_OF_MATERIAL_FACTS",
  FALSE_OR_INVALID_STATUTORY_REGISTRATION = "FALSE_OR_INVALID_STATUTORY_REGISTRATION",
  FORGED_INVOICES_OR_PURCHASE_ORDERS = "FORGED_INVOICES_OR_PURCHASE_ORDERS",
  FORGED_WAREHOUSE_RECEIPTS = "FORGED_WAREHOUSE_RECEIPTS",
  FORGED_LETTERS_OF_CREDIT = "FORGED_LETTERS_OF_CREDIT",
  COLLUSION_WITH_BANK_OFFICIAL = "COLLUSION_WITH_BANK_OFFICIAL",

  DIVERSION_OF_FUNDS = "DIVERSION_OF_FUNDS",
  SIPHONING_OF_FUNDS = "SIPHONING_OF_FUNDS",
  END_USE_VIOLATION = "END_USE_VIOLATION",
  ROUND_TRIPPING_OF_FUNDS = "ROUND_TRIPPING_OF_FUNDS",
  FAKE_SALES_AND_PURCHASES = "FAKE_SALES_AND_PURCHASES",
  CIRCULAR_TRADING = "CIRCULAR_TRADING",

  MISAPPROPRIATION_OF_HYPOTHECATED_ASSETS = "MISAPPROPRIATION_OF_HYPOTHECATED_ASSETS",
  CLANDESTINE_SALE_OF_SECURED_ASSETS = "CLANDESTINE_SALE_OF_SECURED_ASSETS",
  REMOVAL_OR_SUBSTITUTION_OF_STOCK = "REMOVAL_OR_SUBSTITUTION_OF_STOCK",
  INFLATED_OR_FICTITIOUS_STOCK_STATEMENTS = "INFLATED_OR_FICTITIOUS_STOCK_STATEMENTS",
  SUBLETTING_OR_UNAUTHORISED_USE_OF_ASSET = "SUBLETTING_OR_UNAUTHORISED_USE_OF_ASSET",
  CRIMINAL_BREACH_OF_TRUST = "CRIMINAL_BREACH_OF_TRUST",
  FAILURE_TO_COMMENCE_OPERATIONS = "FAILURE_TO_COMMENCE_OPERATIONS",
  UNIT_NON_OPERATIONAL_OR_CLOSED = "UNIT_NON_OPERATIONAL_OR_CLOSED",
  OBSTRUCTION_OF_INSPECTION_OR_AUDIT = "OBSTRUCTION_OF_INSPECTION_OR_AUDIT",

  MANIPULATION_OF_BOOKS_OF_ACCOUNTS = "MANIPULATION_OF_BOOKS_OF_ACCOUNTS",
  FICTITIOUS_ACCOUNTS = "FICTITIOUS_ACCOUNTS",
  CONVERSION_OF_PROPERTY = "CONVERSION_OF_PROPERTY",
  DESTRUCTION_OR_ALTERATION_OF_RECORDS = "DESTRUCTION_OR_ALTERATION_OF_RECORDS",
  WILFUL_FALSIFICATION_OF_RECORDS = "WILFUL_FALSIFICATION_OF_RECORDS",

  OTHERS = "OTHERS",
}

export const FRAUD_ELEMENT_CATEGORIES_LABELS: Record<FraudElementCategories, string> = {
  [FraudElementCategories.FALSE_IDENTITY_OR_KYC_AT_ONBOARDING]: "False Identity or KYC at Onboarding",
  [FraudElementCategories.FORGED_KYC_DOCUMENTS]: "Forged KYC Documents",
  [FraudElementCategories.IMPERSONATION_OF_BORROWER]: "Impersonation of Borrower",
  [FraudElementCategories.FABRICATED_FINANCIAL_STATEMENTS]: "Fabricated Financial Statements",
  [FraudElementCategories.INFLATED_TURNOVER_OR_SALES]: "Inflated Turnover or Sales",
  [FraudElementCategories.INFLATION_OF_PROJECT_COST]: "Inflation of Project Cost",
  [FraudElementCategories.INFLATION_OF_ASSET_OR_MACHINERY_VALUE]: "Inflation of Asset or Machinery Value",
  [FraudElementCategories.OVERVALUATION_OF_COLLATERAL]: "Overvaluation of Collateral",
  [FraudElementCategories.FAKE_OR_SPURIOUS_COLLATERAL]: "Fake or Spurious Collateral",
  [FraudElementCategories.MULTIPLE_MORTGAGE_OF_SAME_PROPERTY]: "Multiple Mortgage of Same Property",
  [FraudElementCategories.MULTIPLE_FINANCING_SAME_COLLATERAL]: "Multiple Financing Same Collateral",
  [FraudElementCategories.SUPPRESSION_OF_EXISTING_LIABILITIES]: "Suppression of Existing Liabilities",
  [FraudElementCategories.CONCEALMENT_OF_MATERIAL_FACTS]: "Concealment of Material Facts",
  [FraudElementCategories.FALSE_OR_INVALID_STATUTORY_REGISTRATION]: "False or Invalid Statutory Registration",
  [FraudElementCategories.FORGED_INVOICES_OR_PURCHASE_ORDERS]: "Forged Invoices or Purchase Orders",
  [FraudElementCategories.FORGED_WAREHOUSE_RECEIPTS]: "Forged Warehouse Receipts",
  [FraudElementCategories.FORGED_LETTERS_OF_CREDIT]: "Forged Letters of Credit",
  [FraudElementCategories.COLLUSION_WITH_BANK_OFFICIAL]: "Collusion with Bank Official",

  [FraudElementCategories.DIVERSION_OF_FUNDS]: "Diversion of Funds",
  [FraudElementCategories.SIPHONING_OF_FUNDS]: "Siphoning of Funds",
  [FraudElementCategories.END_USE_VIOLATION]: "End-Use Violation",
  [FraudElementCategories.ROUND_TRIPPING_OF_FUNDS]: "Round-Tripping of Funds",
  [FraudElementCategories.FAKE_SALES_AND_PURCHASES]: "Fake Sales and Purchases",
  [FraudElementCategories.CIRCULAR_TRADING]: "Circular Trading",

  [FraudElementCategories.MISAPPROPRIATION_OF_HYPOTHECATED_ASSETS]: "Misappropriation of Hypothecated Assets",
  [FraudElementCategories.CLANDESTINE_SALE_OF_SECURED_ASSETS]: "Clandestine Sale of Secured Assets",
  [FraudElementCategories.REMOVAL_OR_SUBSTITUTION_OF_STOCK]: "Removal or Substitution of Stock",
  [FraudElementCategories.INFLATED_OR_FICTITIOUS_STOCK_STATEMENTS]: "Inflated or Fictitious Stock Statements",
  [FraudElementCategories.SUBLETTING_OR_UNAUTHORISED_USE_OF_ASSET]: "Subletting or Unauthorised Use of Asset",
  [FraudElementCategories.CRIMINAL_BREACH_OF_TRUST]: "Criminal Breach of Trust",
  [FraudElementCategories.FAILURE_TO_COMMENCE_OPERATIONS]: "Failure to Commence Operations",
  [FraudElementCategories.UNIT_NON_OPERATIONAL_OR_CLOSED]: "Unit Non-Operational or Closed",
  [FraudElementCategories.OBSTRUCTION_OF_INSPECTION_OR_AUDIT]: "Obstruction of Inspection or Audit",

  [FraudElementCategories.MANIPULATION_OF_BOOKS_OF_ACCOUNTS]: "Manipulation of Books of Accounts",
  [FraudElementCategories.FICTITIOUS_ACCOUNTS]: "Fictitious Accounts",
  [FraudElementCategories.CONVERSION_OF_PROPERTY]: "Conversion of Property",
  [FraudElementCategories.DESTRUCTION_OR_ALTERATION_OF_RECORDS]: "Destruction or Alteration of Records",
  [FraudElementCategories.WILFUL_FALSIFICATION_OF_RECORDS]: "Wilful Falsification of Records",

  [FraudElementCategories.OTHERS]: "Others",
};

export enum FraudElementContributors {
  BORROWER_RESPONSE = "BORROWER_RESPONSE",
  FRAUD_RISK_MANAGEMENT_DEPT = "FRAUD_RISK_MANAGEMENT_DEPT",
  FRAUD_CONTROL_UNIT = "FRAUD_CONTROL_UNIT",
  BRANCH_OFFICE = "BRANCH_OFFICE",
  ZONAL_OFFICE = "ZONAL_OFFICE",
  REGIONAL_OFFICE = "REGIONAL_OFFICE",
  FIELD_GENERAL_MANAGER = "FIELD_GENERAL_MANAGER",
  GENERAL_MANAGER = "GENERAL_MANAGER",
  EXECUTIVE_DIRECTOR = "EXECUTIVE_DIRECTOR",
  MANAGING_DIRECTOR = "MANAGING_DIRECTOR",
}

export interface FraudElementInput {
  party_id: string;
  fraud_element_category: FraudElementCategories;
  details?: string | null;
}

export interface FraudElement {
  fraud_element_id: string;
  party_id: string;
  fraud_element_category: FraudElementCategories;
  details?: string | null;
  created_at?: string | null;
  last_updated_at?: string | null;
}

export interface FraudElementObservation {
  fraud_element_id: string;
  contributor: FraudElementContributors;
  observation?: string | null;
  created_at?: string | null;
  last_updated_at?: string | null;
}

export interface CreateFraudElementsRequest {
  fraud_elements: FraudElementInput[];
}

export interface FraudElementsResponse {
  fraud_elements: FraudElement[];
  observations: FraudElementObservation[];
}

export interface CreateFraudElementsResponse {
  fraud_elements: FraudElement[];
}

export interface UpdateFraudElementRequest {
  details: string;
}

export interface UpsertFraudElementObservationRequest {
  contributor: FraudElementContributors;
  observation: string;
}

export interface MessageResponse {
  message: string;
}

export interface GetFraudElementsParams {
  case_id: string;
}

export interface CreateFraudElementsParams {
  case_id: string;
  request: CreateFraudElementsRequest;
}

export interface UpdateFraudElementParams {
  case_id: string;
  fraud_element_id: string;
  request: UpdateFraudElementRequest;
}

export interface UpsertFraudElementObservationParams {
  fraud_element_id: string;
  request: UpsertFraudElementObservationRequest;
}

export interface DownloadFraudElementsParams {
  case_id: string;
  format: ExportFormat;
}

export enum CommonJobsStatus {
  SUBMITTED = "SUBMITTED",
  IN_PROGRESS = "IN_PROGRESS",
  AVAILABLE = "AVAILABLE",
  FAILED = "FAILED",
}

export interface GeneratedFraudElement {
  fraud_element_category: FraudElementCategories;
  details?: string | null;
}

export interface FraudElementGenerationResult {
  status: CommonJobsStatus;
  fraud_elements: GeneratedFraudElement[];
}

export interface GeneratedFraudElementsResponse {
  fraud_elements: FraudElementGenerationResult | null;
}

export interface GenerateFraudElementsParams {
  case_id: string;
  party_id: string;
}

export interface GetGeneratedFraudElementsParams {
  case_id: string;
  party_id: string;
}

export interface DiscardGeneratedFraudElementsParams {
  case_id: string;
  party_id: string;
}

const FraudElementsAPI = {
  getFraudElements: async (params: GetFraudElementsParams) => {
    const response: AxiosResponse<FraudElementsResponse> =
      await axiosInstance.get(
        `/v1/cases/${params.case_id}/fraud-elements`,
      );

    return response.data;
  },

  createFraudElements: async (
    params: CreateFraudElementsParams,
  ) => {
    const response: AxiosResponse<CreateFraudElementsResponse> =
      await axiosInstance.post(
        `/v1/cases/${params.case_id}/fraud-elements`,
        params.request,
      );

    return response.data;
  },

  updateFraudElement: async (
    params: UpdateFraudElementParams,
  ) => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.patch(
        `/v1/cases/${params.case_id}/fraud-elements/${params.fraud_element_id}`,
        params.request,
      );

    return response.data;
  },

  upsertFraudElementObservation: async (
    params: UpsertFraudElementObservationParams,
  ) => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/fraud-elements/${params.fraud_element_id}/observations`,
        params.request,
      );

    return response.data;
  },

  downloadFraudElements: async (
    params: DownloadFraudElementsParams,
  ) => {
    const response = await axiosInstance.get(
      `/v1/cases/${params.case_id}/fraud-elements/download`,
      {
        params: {
          format: params.format,
        },
        responseType: "blob",
      },
    );

    const exportDate = new Date().toISOString().slice(0, 10);
    const fallbackFileName = `fraud_elements_${exportDate}.${params.format.toLowerCase()}`;

    return extractFileDownload(response, fallbackFileName);
  },

  generateFraudElements: async (
    params: GenerateFraudElementsParams,
  ) => {
    const response: AxiosResponse<GeneratedFraudElementsResponse> =
      await axiosInstance.post(
        `/v1/cases/${params.case_id}/party/${params.party_id}/fraud-elements/generate`,
      );

    return response.data;
  },

  getGeneratedFraudElements: async (
    params: GetGeneratedFraudElementsParams,
  ) => {
    const response: AxiosResponse<GeneratedFraudElementsResponse> =
      await axiosInstance.get(
        `/v1/cases/${params.case_id}/party/${params.party_id}/fraud-elements/generated`,
      );

    return response.data;
  },

  discardGeneratedFraudElements: async (
    params: DiscardGeneratedFraudElementsParams,
  ) => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.delete(
        `/v1/cases/${params.case_id}/party/${params.party_id}/fraud-elements/generated`,
      );

    return response.data;
  },
};

export default FraudElementsAPI;